/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { useCurrentQueueItem, usePlayer } from "@packages/media";
import type { WithButtonWrapper } from "@packages/ui";
import {
  Button,
  Minus10Icon,
  numericPixels,
  RewindIcon,
  Transition,
  TransitionRoot,
} from "@packages/ui";
import { HallowDataTestId } from "@tools/playwright/global";
import { useCallback, useEffect, useState } from "react";

import { coreMediaStyles, useMediaClass } from "../../../utils/media.emotion";
import { FragmentAsChild } from "../../primitives/FragmentAsChild";
import { MediaButton } from "./MediaButton.emotion";

export type BackMediaButtonProps = WithButtonWrapper<{
  canRestart: boolean;
  restartCSS?: Interpolation;
  backCSS?: Interpolation;
}>;

const containerStyle: Interpolation = {
  display: "flex",
  placeItems: "center",
  width: numericPixels[40],
};
const containerExpandedStyle: Interpolation = {
  width: numericPixels[80],
};
const restartButtonStyle: Interpolation = {
  pointerEvents: "all",
  transform: "translateX(-50%)",
};
const restartReducedStyle: Interpolation = {
  transform: "translateX(-10%)",
};

export const BackMediaButton = ({
  canRestart,
  Wrapper = FragmentAsChild,
  backCSS = {},
  restartCSS = {},
}: BackMediaButtonProps) => {
  const [value, setValue] = useState<"restart" | "back">(
    canRestart ? "restart" : "back",
  );
  const t = useTranslations();
  const player = usePlayer();
  const { currentItem } = useCurrentQueueItem();
  const mediaClass = useMediaClass();

  useEffect(() => {
    setValue(canRestart ? "restart" : "back");
  }, [canRestart]);

  const handleBack = useCallback(
    (e) => {
      e.stopPropagation();
      player?.skipBack();
    },
    [player],
  );

  const handleRestart = useCallback(
    (e) => {
      e.stopPropagation();
      player?.restart();
    },
    [player],
  );

  return (
    <>
      <TransitionRoot
        value={value}
        defaultValue={canRestart ? "restart" : "back"}
        kind={"fade"}
        css={[
          containerStyle,
          mediaClass.includes("expanded") || mediaClass.includes("fullscreen")
            ? containerExpandedStyle
            : null,
        ]}
      >
        <Transition value={"restart"} css={coreMediaStyles.unclickable}>
          <Wrapper asChild>
            <Button
              variant={"lighten"}
              data-testid={HallowDataTestId.MediaRestart}
              onClick={handleRestart}
              css={[
                restartButtonStyle,
                mediaClass.includes("mobile") &&
                mediaClass.includes("portrait") &&
                mediaClass.includes("fullscreen")
                  ? restartReducedStyle
                  : null,
                restartCSS,
              ]}
              size={mediaClass.includes("fullscreen") ? "l" : "s"}
            >
              {t("audio_player_restart")}
            </Button>
          </Wrapper>
        </Transition>
        <Transition value={"back"}>
          <Wrapper asChild>
            <MediaButton
              largeIcon={
                currentItem?.selected_audio?.show_music_controls ? (
                  <RewindIcon.XL />
                ) : (
                  <Minus10Icon.XL />
                )
              }
              smallIcon={
                currentItem?.selected_audio?.show_music_controls ? (
                  <RewindIcon.Small />
                ) : (
                  <Minus10Icon.Medium />
                )
              }
              css={backCSS}
              data-testid={HallowDataTestId.MediaSkipBack}
              onClick={handleBack}
              tabIndex={0}
            />
          </Wrapper>
        </Transition>
      </TransitionRoot>
    </>
  );
};
